// Virginia Commonwealth University: public college page, official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - VCU's Common Data Set 2024-2025 (Institutional Research and Decision
//    Support). Departure: it is the newest edition VCU posts; there is no
//    2025-2026 edition on the IRDS Common Data Set page, so the counts are
//    for the Fall 2024 class. C1 gives applicants, admits and enrollees by
//    gender only (men, women, another gender, unknown); the totals here are
//    their sums (21,201 applied, 19,572 admitted, 4,275 enrolled). The C1
//    residency table totals do not match them (24,804 applied, 20,769
//    admitted, 4,449 enrolled), so no residency rows are shown. C21/C22: no
//    early decision, no early action. The edition's C8 answers for Fall 2026,
//    not Fall 2027, so no CDS line is shown for testing.
//  - VCU Admissions: Deadlines, First-year applicants, First-year
//    requirements and Guaranteed University Admission (the Fall 2027
//    application). VCU publishes a decision date only for the January 15
//    deadline (by April 1).
//  - VCU Student Financial Services, Understanding Cost of Attendance: the
//    estimated 2026-2027 cost of attendance for an undergraduate living on
//    campus, item by item, with VCU's own totals ($44,673 and $69,413).
//  - College Scorecard API, school id 234030 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'VCU Common Data Set 2024-2025',
        url: 'https://irds.vcu.edu/institutional-reporting/common-data-set/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'VCU Admissions, Deadlines',
        url: 'https://admissions.vcu.edu/apply-to-vcu/deadlines/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'VCU Admissions, First-year applicants',
        url: 'https://admissions.vcu.edu/apply-to-vcu/undergraduate/first-year/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'VCU Admissions, First-year requirements',
        url: 'https://admissions.vcu.edu/apply-to-vcu/undergraduate/first-year/first-year-requirements/',
        read: '2026-10-01',
    },
    gua: {
        label: 'VCU Admissions, Guaranteed University Admission',
        url: 'https://admissions.vcu.edu/apply-to-vcu/undergraduate/guaranteed-university-admission/',
        read: '2026-10-01',
    },
    coa: {
        label: 'VCU Student Financial Services, Understanding Cost of Attendance',
        url: 'https://sfs.vcu.edu/financial-aid/how-aid-works/understanding-cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?234030-Virginia-Commonwealth-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'virginia-commonwealth-university',
    kbId: 'virginia_commonwealth_university',
    name: 'Virginia Commonwealth University',
    shortName: 'VCU',
    aliases: ['VCU'],
    location: 'Richmond, VA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (VCU visual identity standards); decoration only.
    branding: {
        primary: '#FFB300',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/virginia_commonwealth_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 21201,
        admitted: 19572,
        enrolled: 4275,
        source: 'cds',
        where: 'section C1 (all gender lines added together; the 2024-2025 edition is the newest VCU posts)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'VCU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'VCU has no early decision or early action plan. Its November 1 date is a scholarship deadline: first-year applicants who apply by then are considered for university-level scholarships.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply through the Common App to one undergraduate degree program. Some programs and paths ask for more:',
        rows: [
            { school: 'Guaranteed University Admission', requirement: 'First-year applicants with a high school GPA of 3.5 or higher (weighted, if your transcript shows it) are guaranteed admission if they apply with all materials by January 15, 2027. There is nothing extra to send. School of the Arts applicants are not eligible.', source: 'gua' },
            { school: 'School of the Arts', requirement: 'Supplemental materials described on the School of the Arts website, reviewed by the school\'s own committee.' },
            { school: 'College of Engineering', requirement: 'To be competitive, aim for at least a B average. Applicants not admitted directly are considered for the undeclared major in the College of Humanities and Sciences.' },
            { school: 'Honors College', requirement: 'A separate Honors College application. To be competitive: a 3.5 GPA and an SAT of 1330 or ACT of 29.' },
            { school: 'Honors College Guaranteed Admission Programs', requirement: 'Entry to medical school and other VCU health sciences programs. All applications are due November 1, and test scores are required.' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular decision', value: 'January 15' },
        rows: [
            { plan: 'Scholarship deadline', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Regular decision', deadline: 'January 15', decision: 'By April 1', binding: false },
        ],
        note: 'Apply on the Common App by November 1 to be considered for university-level scholarships. Applications submitted by January 15 get a decision by April 1; decisions on later applications are sent on a rolling basis. The application fee is $75, and a waiver can be requested in the applicant portal.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Every applicant is eligible for test-optional review, whatever the GPA, and is still considered for scholarships. If you have scores and want them read, you can send them. Scores are required only for the Honors College Guaranteed Admission Programs.',
        source: 'requirements',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and mandatory fees', amounts: [17963, 41127] },
            { item: 'Food and housing (on campus)', amounts: [19724, 19724] },
            { item: 'Books and supplies', amounts: [1290, 1290] },
            { item: 'Personal and miscellaneous', amounts: [4322, 4322] },
            { item: 'Transportation', amounts: [1304, 2880] },
            { item: 'Loan fees', amounts: [70, 70] },
        ],
        totals: [44673, 69413],
        note: 'VCU\'s estimate for an undergraduate living on campus, fall and spring. Program and major fees and the New Student Transition Fee are not included. A first-time undergraduate\'s cost of attendance also includes a $1,000 computer allowance.',
        source: 'coa',
        netPrice: {
            average: 25037,
            byIncome: [
                { income: '$0 to $30,000', amount: 18927 },
                { income: '$30,001 to $48,000', amount: 18347 },
                { income: '$48,001 to $75,000', amount: 20373 },
                { income: '$75,001 to $110,000', amount: 27067 },
                { income: 'Over $110,000', amount: 38273 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from VCU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
